"use client";

import { Person, PersonData, Team } from "@/sources/kicker-amateur/types";
import React, { useRef } from "react";
import {
  ringsideCondensedBookBase64,
  ringsideCondensedSemiBase64,
  ringsideCompressedBookBase64,
  ringsideCompressedBoldBase64,
  knockoutHTF71FullMiddlewtBase64,
  knockoutHTF50WelterweightBase64
} from "@/fonts/base64fonts";
import defaultTeamLogo from "@/assets/icon/default-team-logo.png";
import backgroundImage from "@/assets/generator/AdobeStock_618346374.jpeg";
import VereinsheimLogo from "@/assets/branding/logo.svg";
import Brush01 from '@/assets/generator/brush-01.svg';
import { usePwa } from "@/app/pwa/lib/pwa-context";
import Button from "@/app/pwa/components/Button";
import { TeamName } from "@/app/pwa/components/team/TeamName";


function getContrastYIQ(hexcolor: any) {
  var r = parseInt(hexcolor.substr(0, 2), 16);
  var g = parseInt(hexcolor.substr(2, 2), 16);
  var b = parseInt(hexcolor.substr(4, 2), 16);
  var yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "#000000" : "#FFFFFF";
}

interface PlayerPosterSVGProps {
  person: PersonData | Person;
  team: Team;
}

export const PlayerPosterSVG: React.FC<PlayerPosterSVGProps> = ({
  person,
  team
}: PlayerPosterSVGProps) => {
  const svgRef = useRef<SVGSVGElement>(null);

  const pwa = usePwa();
  const teamColor = pwa.PrimaryColor || "#CC0000";
  const teamContrastColor = getContrastYIQ(teamColor.replace("#", ""));
  const teamSecondaryColor = pwa.SecondaryColor || "#CC0000";

  


  // Preview size
  const previewWidth = 640;
  const previewHeight = previewWidth * 800/640;
  const fullWidth = 1600;
  const fullHeight = 2000;

  const handleDownloadPNG = async () => {
    const svg = svgRef.current;
    if (!svg) return;

    // Clone the SVG and set full size for PNG export
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute("width", fullWidth.toString());
    clone.setAttribute("height", fullHeight.toString());

    // Convert all image URLs to base64
    const images = clone.querySelectorAll('image');
    const imagePromises = Array.from(images).map(async (img) => {
      const href = img.getAttribute('href');
      if (!href || href.startsWith('data:')) return;

      try {
        const response = await fetch(href);
        const blob = await response.blob();
        const reader = new FileReader();
        return new Promise((resolve) => {
          reader.onloadend = () => {
            img.setAttribute('href', reader.result as string);
            resolve(null);
          };
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error loading image:', error);
      }
    });

    // Wait for all images to be converted
    await Promise.all(imagePromises);

    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(clone);
    const svg64 = btoa(unescape(encodeURIComponent(source)));
    const image64 = `data:image/svg+xml;base64,${svg64}`;
    
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.src = image64;
    
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = fullWidth;
      canvas.height = fullHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      
      ctx.fillStyle = "#f5f5f5";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, fullWidth, fullHeight);
      
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        
        // Format date and time
        // const date = person.DateOfBirth ? new Date(person.DateOfBirth) : new Date();
        // const formattedDate = date.toISOString().split('T')[0];
        // const formattedTime = date.toTimeString().split(' ')[0].replace(/:/g, '-');
        
        // Format team names (remove special characters and spaces)
        const firstName = person.FirstName?.replace(/[^a-zA-Z0-9]/g, '') || '';
        const lastName = person.LastName?.replace(/[^a-zA-Z0-9]/g, '') || '';
        
        link.download = `${firstName}-${lastName}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, "image/jpeg");
    };
  };

  

  const meta = [
    <>
    {team.OfficalName}
    {<TeamName team={team} teamTypeName /> && (
      <>
        {" – "}
        <TeamName team={team} teamTypeName />
      </>
    )}
    </>
    ,
    person.FunctionType?.OfficalName === "Ohne Position" ? "" : person.FunctionType?.OfficalName,
  ].filter(Boolean);


  const aspectImage = person?.MediaServiceImages?.find(Boolean)
  const noCache = true

  function dynamicFontSize(name?:string) {
    return name ?
      -4.722 * name.length + 136.333
    :
      76
  }

  const playerNumber = "ShirtNumber" in person ? person.ShirtNumber || "-" : "-";

  return (
    <div>
      <div className="aspect-[640/800]">
        <svg
          className="w-full h-full"
          ref={svgRef}
          viewBox="0 0 640 800"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <style>
              {`
                @font-face {
                  font-family: 'Ringside Condensed';
                  src: url(${ringsideCondensedBookBase64}) format('woff2');
                  font-weight: 400;
                  font-style: normal;
                }
                @font-face {
                  font-family: 'Ringside Condensed';
                  src: url(${ringsideCondensedSemiBase64}) format('woff2');
                  font-weight: 600;
                  font-style: normal;
                }
                @font-face {
                  font-family: 'Ringside Compressed';
                  src: url(${ringsideCompressedBookBase64}) format('woff2');
                  font-weight: 400;
                  font-style: normal;
                }
                @font-face {
                  font-family: 'Ringside Compressed';
                  src: url(${ringsideCompressedBoldBase64}) format('woff2');
                  font-weight: 700;
                  font-style: normal;
                }
                @font-face {
                  font-family: 'Knockout HTF71 Full Middlewt';
                  src: url(${knockoutHTF71FullMiddlewtBase64}) format('woff2');
                  font-weight: normal;
                  font-style: normal;
                }
                @font-face {
                  font-family: 'Knockout HTF50 Welterweight';
                  src: url(${knockoutHTF50WelterweightBase64}) format('woff2');
                  font-weight: normal;
                  font-style: normal;
                }
                .condensed {
                  font-family: 'Ringside Condensed', sans-serif;
                }
                .compressed {
                  font-family: 'Ringside Compressed', sans-serif;
                }
                .knockout-htf71-full-middlewt {
                  font-family: 'Knockout HTF71 Full Middlewt', sans-serif;
                }
                .knockout-htf50-welterweight {
                  font-family: 'Knockout HTF50 Welterweight', sans-serif;
                }
              `}
            </style>
            <filter id="team-icon-bg">
              <feGaussianBlur in="SourceGraphic" stdDeviation="70" />
              {/* <feGaussianBlur in="SourceGraphic" stdDeviation="50" /> */}
            </filter>
            <filter id="background">
              <feColorMatrix type="matrix" values="
                0.33 0.33 0.33 0 0
                0.33 0.33 0.33 0 0
                0.33 0.33 0.33 0 0
                0 0 0 1 0
              "/>
            </filter>
            <filter id="background-darken">
              <feComponentTransfer>
                <feFuncR type="linear" slope="0.5"/>
                <feFuncG type="linear" slope="0.5"/>
                <feFuncB type="linear" slope="0.5"/>
              </feComponentTransfer>
            </filter>
            {/* <filter id="textNoise" x="0" y="0" width="100%" height="100%">
              <feTurbulence type="fractalNoise" baseFrequency="0.09" numOctaves="2" seed="7" result="noise"/>
              <feColorMatrix type="matrix" values="1 0 0 0 0
                                                  0 1 0 0 0
                                                  0 0 1 0 0
                                                  0 0 0 0.25 0" result="noiseAlpha"/>
              <feComposite operator="in" in="noiseAlpha" in2="SourceAlpha" result="noiseMasked"/>
              <feBlend in="SourceGraphic" in2="noiseMasked" mode="multiply"/>
            </filter> */}
            <filter id="textNoise">
              <feTurbulence id="turbulenceNode" type="turbulence" baseFrequency="1.404" numOctaves="1" seed="75" stitchTiles="noStitch" result="turb"></feTurbulence>
              <feColorMatrix in="turb" type="matrix" values="
                1 0 0 0 0
                0 1 0 0 0
                0 0 1 0 0
                0 0 0 0.5 0
              " result="mono"></feColorMatrix>
              <feComponentTransfer in="mono" result="contrastAdjusted">
                <feFuncR type="gamma" exponent="10" amplitude="1"></feFuncR>
                <feFuncG type="gamma" exponent="10" amplitude="1"></feFuncG>
                <feFuncB type="gamma" exponent="10" amplitude="1"></feFuncB>
              </feComponentTransfer>
              <feComposite operator="in" in="noiseAlpha" in2="SourceAlpha" result="noiseMasked"/>
              <feBlend in="SourceGraphic" in2="noiseMasked" mode="multiply"/>
            </filter>
            <filter id="textShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.3"/>
            </filter>
            <path d="M563.713572,5.12454891 C565.559747,5.36874979 571.683201,20.9201088 597.086395,8.30830812 C603.319523,8.38134062 615.991416,18.0672766 622.512438,9.8967651 L640.791404,17.040257 C640.613184,18.9756184 626.273335,10.823365 629.664083,23.3895204 C633.036553,35.8689497 652.951487,15.734801 644.76251,40.0546252 C646.16999,40.8899345 650.168514,35.622465 650.323885,35.2938187 C656.218852,34.6867361 650.488396,37.9458116 651.914155,40.8488537 C652.652169,42.3528668 657.815977,46.6800427 662.994637,51.2457154 L663.752083,51.9152652 C668.66952,56.2745048 673.405706,60.7354601 674.164228,63.0735579 C676.513075,70.3265985 669.187779,73.5308997 669.393417,74.1836277 C669.800124,75.4616966 688.179625,72.4491057 678.930468,90.8487325 C682.161275,97.3212382 682.851306,102.638918 688.462949,103.551824 C689.906988,113.027791 692.475182,121.80995 693.233759,131.326998 C688.538764,136.47713 687.553082,138.3051 687.277798,139.884322 L687.247982,140.072832 L687.223834,140.260133 C687.220218,140.291315 687.21679,140.322496 687.213526,140.3537 L687.195721,140.541402 C687.193018,140.572799 687.190431,140.604268 687.187937,140.635834 L687.173409,140.833464 C687.127518,141.497858 687.099877,142.225812 686.877249,143.235861 C680.717237,171.152536 697.707537,160.101805 698,166.250229 C673.894613,186.717589 687.489594,186.439153 683.696709,202.757353 C683.381396,204.117583 680.566436,203.396387 680.520738,203.551581 C680.072903,204.989409 679.880974,210.142765 678.930468,211.489302 C680.502459,219.180538 679.730173,222.37571 680.520738,230.537092 C682.983829,230.60556 681.101095,233.148004 683.696709,233.714006 C685.299401,250.362524 682.888638,266.595775 680.084157,282.75355 L679.626497,285.373121 C679.549835,285.809624 679.47303,286.246086 679.39615,286.682512 L678.934568,289.300702 L678.474659,291.918375 C676.413979,303.697218 674.50831,315.470102 674.164228,327.369065 C673.295977,357.225666 683.824661,377.492186 666.217447,405.148683 C668.625701,422.5715 663.219696,441.030465 664.627177,458.325475 C665.070442,463.784655 678.555749,474.237433 663.036907,485.310986 C675.696466,483.425805 675.06651,489.902511 672.781367,497.782986 L672.619399,498.334976 C672.290677,499.443163 671.934998,500.57553 671.583967,501.713175 L671.341334,502.50382 C670.296565,505.931592 669.36015,509.367528 669.393417,512.296497 C669.399632,512.784841 670.705607,516.849077 672.13204,521.247488 L672.336509,521.877756 C672.370681,521.983063 672.40489,522.088472 672.439119,522.193939 L672.711075,523.031881 C672.813754,523.348287 672.916239,523.664173 673.0181,523.978359 L673.220861,524.604114 C674.565358,528.756035 675.747955,532.465684 675.749928,532.932744 C675.809335,543.641135 667.720892,552.464375 667.807717,553.568991 C667.972228,555.732579 687.334223,575.017725 682.106439,593.253029 C678.19474,606.896413 603.520591,605.700506 664.627177,612.300819 C664.573746,613.673268 664.540113,615.064248 664.508062,616.465214 L664.484036,617.517677 L664.445374,619.101642 L664.415361,620.159964 C664.145191,628.984344 663.314255,637.906788 657.475531,644.841364 C648.930114,640.217494 647.600319,651.971163 647.147915,652.779085 C646.622395,653.714814 648.82958,654.988318 648.733615,655.157206 C644.703103,662.496973 625.217725,670.279499 647.147915,674.999224 C627.328945,688.596964 609.081967,683.589673 597.88153,691.668894 C598.425329,695.758714 615.785778,686.474457 636.815729,689.286208 L636.815729,692.463122 C604.238041,695.635472 604.238041,695.635472 571.660353,698.812386 C567.734944,705.207294 560.121755,699.168419 546.23431,700.396278 C538.464744,701.085203 524.038699,702.649337 509.535291,704.208618 L507.51172,704.426072 C488.969849,706.417489 470.802041,708.333999 466.775642,708.333999 C464.840972,708.333999 459.790992,707.20213 454.612586,706.065908 L453.77219,705.881796 L452.836559,705.677859 C443.029907,703.548161 433.989756,701.866651 446.11584,708.333999 C405.180094,716.577543 403.923415,706.832268 401.620266,706.745542 C370.94359,705.608973 339.398663,706.850526 308.653441,706.745542 C308.475222,706.836832 307.684656,709.543599 306.268036,709.922456 C303.19717,710.739507 260.141064,702.09803 242.70293,705.75422 C240.326664,706.251754 191.722892,726.340258 208.534972,715.275833 L208.534972,713.687376 C192.99328,718.881813 183.140917,718.247343 194.23168,713.687376 C194.172045,713.509017 194.116883,713.344035 194.065522,713.190421 L193.922174,712.761677 C193.744461,712.230136 193.616842,711.848355 193.496379,711.487793 L193.376711,711.129542 C193.226133,710.678668 193.064372,710.193998 192.807569,709.424474 L192.64141,708.92657 C189.555233,710.003643 184.996795,711.735145 180.403647,713.47773 L179.255469,713.913234 L178.379579,714.24519 L177.507993,714.57511 C173.487293,716.095887 169.676666,717.519165 167.072069,718.399226 L166.58328,718.562658 C165.400296,718.953681 164.514724,719.210787 164.039397,719.283481 C149.512188,721.515537 149.84578,718.370585 164.039397,712.103484 L164.039397,708.92657 C161.868366,709.849999 152.757837,710.426087 143.24097,710.885089 L142.272006,710.931372 C141.787079,710.954321 141.301673,710.976987 140.816649,710.999402 L139.688953,711.051124 L138.614876,711.099774 C130.934712,711.445911 123.682249,711.739634 120.334388,712.103484 C119.744891,712.167387 118.835513,716.72279 117.158418,716.86429 C113.559746,717.163267 103.684534,716.670297 93.4882982,715.951954 L92.2125275,715.861029 L90.4512549,715.732151 L89.2796247,715.644328 C80.5063426,714.98054 72.103421,714.207048 67.8920328,713.687376 C52.2817953,728.586007 44.8285466,707.79913 62.3306572,698.610728 C62.2762421,698.414804 61.2791515,698.403 59.8313019,698.399964 L59.3326654,698.399035 C55.4778023,698.390901 49.147304,698.315789 48.0273658,695.433814 C45.6739488,689.376681 71.3604667,685.958665 25.7772937,685.31963 C26.541158,683.236136 29.1256398,682.097339 31.7623644,681.110189 L32.3484444,680.892997 C32.4460282,680.857107 32.5435039,680.821344 32.6407818,680.785668 L33.0663025,680.629477 C37.0913233,679.148584 40.5434542,677.716718 36.9046146,673.410768 C48.2691508,653.036205 35.0751144,659.858678 28.9760721,663.115328 L28.0766415,663.595418 C26.3772181,664.498013 25.5907263,664.860318 26.7819256,663.647532 L26.9486026,663.480325 C28.4918553,661.95231 32.6215237,658.354021 40.8757201,651.190628 C98.7103705,643.047504 14.9241573,651.154112 46.4370957,640.870222 C21.2669587,636.442626 25.3340287,624.593102 15.4496776,625.793574 C5.40081566,619.88707 55.1790115,606.325847 51.207906,613.884711 C25.0004376,623.168969 61.7091723,619.26173 90.9372399,610.712362 C91.6294187,608.181923 83.236017,609.404332 74.2690256,610.822944 L72.6823937,611.0746 C72.2622813,611.141285 71.8420102,611.207942 71.4224448,611.27421 L70.5845109,611.406119 C63.195598,612.564837 56.2067795,613.526854 54.3838764,612.300819 C103.385216,593.631885 98.664673,608.416402 59.945252,595.63115 C49.297754,601.295733 30.8725556,600.401085 18.6302177,598.808064 C12.8324796,605.702109 10.8142901,605.544247 9.0129717,604.630238 L8.85214459,604.546533 C8.8254051,604.532301 8.79869024,604.517942 8.77198815,604.503478 L8.53169446,604.371013 C8.3178381,604.251735 8.10252502,604.129715 7.87969035,604.015671 L7.71105929,603.9318 L7.53903905,603.851925 C7.27818252,603.735611 7.00396854,603.636173 6.70776177,603.56887 C-4.85368461,585.730681 3.52722158,575.757179 3.53179133,574.200674 C3.53745781,570.878243 2.56293664,568.14185 1.66015976,565.600411 L1.4866071,565.110849 C1.44191659,564.984293 1.39757878,564.858165 1.35372725,564.732416 L1.22376506,564.356257 C-0.528796569,559.229609 -1.23938985,554.656776 8.29803187,547.215163 C-1.55433113,538.519731 1.01843342,517.422466 3.53179133,505.153005 C21.0476111,499.78968 5.38253669,468.139218 5.12206142,493.248706 L1.94152123,493.248706 C-4.20935103,482.284702 6.01773078,418.600357 8.29803187,401.971769 C9.90201119,390.291132 12.9591684,384.6585 13.0642724,383.718207 C13.2603905,381.974176 12.3696082,379.958333 11.418663,377.885301 L11.1326012,377.26189 C9.89407761,374.55487 8.72739718,371.787472 9.88830196,369.431223 C11.0947138,366.98007 35.076718,363.899011 13.0642724,351.177661 C7.41607173,334.311717 9.53643186,317.258627 13.0642724,300.383554 C9.30794477,298.128676 13.4481307,282.10717 16.2448126,282.919656 C19.8137808,280.619132 18.7261823,283.261996 21.0110531,281.335764 C21.1801336,279.395838 19.6858281,279.907066 19.4207831,279.747307 C16.1139518,277.758278 16.3605248,277.182619 16.9906627,276.582522 L17.0829494,276.496472 L17.1880411,276.400322 C17.559375,276.059673 17.9508495,275.668137 17.8350827,274.9865 C15.865524,263.470187 17.7391181,252.91699 19.4207831,241.651726 C21.3949114,228.441972 30.8268582,204.44623 27.3675638,177.360299 C26.0788966,167.258991 18.9683786,153.410202 16.2448126,143.235861 C13.8228495,134.18896 8.58135585,127.123065 9.88830196,117.040015 C10.1355175,115.13638 12.2606132,110.820451 14.6366449,106.295028 L15.114715,105.387803 C17.4368987,100.99569 19.8922156,96.5450434 21.0110531,94.0256464 C23.8214443,87.7083348 25.1238206,85.3210847 24.1915933,77.3559772 C42.4248625,72.5084446 47.6846352,63.0416062 48.8225008,63.8632218 C50.5772816,65.1412907 49.0189997,68.8979002 49.6176359,69.4228213 C58.8302351,77.5020422 61.0465598,57.2172639 75.8388135,63.8632218 C77.6354874,54.9932615 86.6021017,54.6420064 96.8669435,55.5762877 L97.5768505,55.6426711 L98.290446,55.7126035 C98.8865269,55.7722128 99.486004,55.8349697 100.087763,55.8995012 L101.285658,56.0296093 L102.487612,56.1619938 C102.888725,56.2062357 103.290191,56.2504585 103.691686,56.2942639 L104.895939,56.4240288 C111.716877,57.1477497 118.452934,57.6356488 123.514928,55.930066 C126.855409,54.8026267 125.42965,45.8241931 133.842544,47.9923456 L150.531241,47.9923456 C181.281032,24.2932977 195.360406,38.8495888 222.043128,36.8822757 C222.71488,36.8320659 224.519928,33.7783943 225.219098,33.7053618 C228.02492,33.4086672 271.071886,29.4877346 258.591922,25.7676414 C257.211151,25.35644 254.499972,25.8933559 251.802905,26.6359819 L251.156568,26.8177816 C247.825674,27.773559 244.681864,28.954314 244.28863,28.9445553 C243.667145,28.9262972 241.752424,25.7995931 241.11266,25.7676414 C238.492187,25.6369132 235.590014,25.5532764 232.664499,25.4716541 L231.566842,25.4410521 L231.116873,25.4284372 L229.769899,25.3898862 C215.439002,24.9706945 202.606913,24.2401884 222.043128,17.829921 C244.28863,23.2662781 266.264518,24.0513775 288.788775,21.0068349 C291.416376,21.093561 286.316544,33.0069884 338.05059,17.829921 C359.322737,30.2271887 389.981134,8.55479282 415.923557,17.829921 C433.681573,11.3939315 457.96061,5.75217044 474.722423,13.0691145 C480.356333,11.5694584 483.32694,10.7787307 486.152276,10.0261895 L486.802844,9.85289877 C488.293072,9.45592825 489.796843,9.05520755 491.686591,8.55152943 L493.096204,8.17580981 C494.618398,7.77007062 496.391307,7.29747185 498.558195,6.71985113 C548.414077,9.01581051 500.354104,8.70998689 485.845174,13.0691145 C460.780141,20.6051562 519.277404,10.8963975 522.398538,19.418378 C534.640875,5.03097445 529.207453,17.8207919 538.287529,17.829921 C555.131597,11.836691 545.896149,9.13905286 547.82458,8.30830812 C550.703517,7.062191 549.42399,12.2429343 552.590821,11.4806576 C553.324566,11.3043633 555.396526,10.0707519 557.558963,8.7364774 L558.124308,8.38675133 L558.688836,8.0362324 C561.126242,6.52105067 563.401967,5.08984048 563.713572,5.12454891 Z M23.3955443,648.780243 C33.448976,647.383496 19.0542898,656.521688 17.8341687,652.746821 C17.6970765,652.322319 22.9979768,648.835017 23.3955443,648.780243 Z M678.135333,601.159398 C688.677727,598.443501 657.416124,628.96196 670.983687,608.30289 C671.28529,607.843191 672.884456,606.15614 674.497505,604.536754 L674.936808,604.097754 C676.468819,602.573936 677.907151,601.219041 678.135333,601.159398 Z M681.313131,496.391987 C688.227151,496.41481 673.72279,519.821727 675.751756,505.123936 C675.843151,504.466643 680.746483,496.391987 681.313131,496.391987 Z M212.50562,1.13574717 C255.287541,-1.85858553 217.482069,1.254425 214.891025,8.27923908 C214.791322,8.54647165 215.746662,9.08893417 216.607408,9.84385488 L216.767408,9.98787693 C218.192385,11.3062072 219.143378,13.2183735 214.09589,15.422731 C211.920693,15.6098768 210.261877,4.002273 175.956826,24.9443438 C162.969621,23.2052573 148.433272,26.9801249 135.432357,28.1212578 C118.130219,29.6417946 106.774681,29.7699228 95.9743845,29.2840451 L94.6268493,29.2201688 L93.2827579,29.1502344 C82.5375612,28.567428 71.8983253,27.4354826 55.9736896,26.5328008 C92.8195165,18.7411454 130.675256,11.593089 167.214911,5.10232511 C152.984735,16.3721536 210.426388,-0.502919659 201.382869,5.8965536 C200.668893,6.40266886 199.007141,7.13866397 197.19218,7.89275558 L196.670137,8.1085198 C196.495191,8.18048338 196.319541,8.25242816 196.143874,8.32417119 L195.61751,8.53861206 C189.229498,11.135827 183.733384,13.2205271 212.50562,5.8965536 Z M257.798615,14.6285025 C257.798615,16.4314925 249.102396,15.2949241 252.237239,13.834274 C253.242582,13.3641272 257.798615,13.7384188 257.798615,14.6285025 Z M517.628641,10.6600987 C518.638554,10.189952 523.190017,10.5642436 523.190017,11.4543272 C523.190017,13.2527527 514.493798,12.1207488 517.628641,10.6600987 Z M255.413209,5.89746651 C256.409413,5.76053056 257.506151,5.71032071 257.798615,6.691695 C259.155828,11.2699202 248.220436,6.88340533 255.413209,5.89746651 Z" id="path-1"></path>
          </defs>
          <rect x="0" y="0" width="600" height="800" fill="#f5f5f5" />
          <image
            x="0"
            y="0"
            width="100%"
            height="146%"
            href={backgroundImage.src}
            preserveAspectRatio="xMinYMid slice"
            filter="url(#background) url(#background-darken)"
          />

            <image
                width="100%"
                height="100%"
                href={team.MediaServiceImages?.find(Boolean)?.AbsoluteUrl ?? ((team && team.Id) ? `/api/image/team/${team.Id!.toString()}` : defaultTeamLogo.src)}
                preserveAspectRatio="xMidYMid slice"
                filter="url(#team-icon-bg)"
                opacity="0.15"
              />
          <g transform="translate(-5, 100) scale(0.4)" transform-origin="center top">
            <use id="Combined-Shape" fill="#010202" xlinkHref="#path-1"></use>
            <mask id="mask-2" fill="white">
                <use xlinkHref="#path-1"></use>
            </mask>
            <image
                href={aspectImage?.AbsoluteUrl ?? `/api/image/person/${person?.Id}${noCache ? "?no_cache=true" : ""}`}
                x="0"
                y="0"
                width="702"
                height="940"
                preserveAspectRatio="xMidYMid slice"
                mask="url(#mask-2)"
              />
            <image
                  width="160"
                  x="-35"
                  href={team?.MediaServiceImages?.find(Boolean)?.AbsoluteUrl ?? ((team && team.Id) ? `/api/image/team/${team.Id!.toString()}` : defaultTeamLogo.src)}
                  preserveAspectRatio="xMidYMid meet"
                />
            <g transform-origin="middle middle" transform="translate(334, 650)">
              <g transform-origin="middle middle" transform="scale(2.4)">
                <rect
                  fill={teamColor}
                  x="-26"
                  width="52"
                  height="52"
                  rx="8"
                  ry="8"
                  filter="url(#textShadow)"
                  />

                {playerNumber != "-" && playerNumber >= 10 ? 
                  <text y="38" x="0" fill={teamContrastColor} fontSize="37" className="knockout-htf71-full-middlewt" textAnchor="middle">
                    {playerNumber}
                  </text>
                :
                  <text y="41" x="0" fill={teamContrastColor} fontSize="45" className="knockout-htf71-full-middlewt" textAnchor="middle">
                    {playerNumber}
                  </text>
                }
              </g>
            </g>
          </g>

          <g transform={`translate(0, 470)`}>
            <text x="50%" y="0" textAnchor="middle" fontSize={50}className="knockout-htf71-full-middlewt" fill="#FFF" style={{textTransform: "uppercase"}} filter="url(#textNoise) url(#textShadow)">
            {person.FirstName}
            </text>
            <text x="50%" y="10" textAnchor="middle" dominantBaseline="hanging" fontSize={dynamicFontSize(person.LastName)}className="knockout-htf71-full-middlewt" fill="#FFF" style={{textTransform: "uppercase"}} filter="url(#textNoise) url(#textShadow)">
            {person.LastName}
            </text>
            <g transform={`translate(0, ${40 + dynamicFontSize(person.LastName) * 0.9} )`}>
              {meta.map((item, index) => (
                <text key={index} x="50%" y={`${index * 36}`} textAnchor="middle" fontSize="32" fill="#FFF" className="compressed" filter="url(#textNoise) url(#textShadow)">
                  {item}
                </text>
              ))}

            </g>
          </g>

         

          
          
          {/* Footer */}
          <g transform="translate(-200, -520) scale(1) rotate(5)">
            <Brush01 fill={teamColor} />
          </g>
          <g transform="translate(300,480) scale(1) rotate(12)">
            <Brush01 fill={teamSecondaryColor} />
          </g>
          <g transform="translate(240, 750)">
            <rect
              x={"-10"}
              y={"-10"}
              width={"180"}
              height={"60"}
              fill="#cc0000"
            />
            <VereinsheimLogo width={160} height={40} />
          </g>
        </svg>
      </div>
      <div className="mt-4">
        <Button
          onClick={handleDownloadPNG}
          color="primary"
          className="w-full justify-center"
        >
          Herunterladen
        </Button>
      </div>
    </div>
  );
};

export default PlayerPosterSVG; 
